import React, { Component, CSSProperties } from "react";
import ReactDOM from "react-dom";
import {
  ToastsContainer,
  ToastsContainerPosition,
  ToastsStore,
} from "react-toasts";
import {
  AiApiToolOption,
  AiModelOption,
  AiProcessPromptConfig,
  deleteAiVerificationProcess,
  getAiVerificationApiTools,
  getAiVerificationModels,
  getAiVerificationPrompts,
  getAiVerificationSettings,
  saveAiVerificationApiTools,
  saveAiVerificationModels,
  saveAiVerificationPrompts,
  saveAiVerificationSettings,
  DEFAULT_CLOSE_REMARK_OPTIONS,
  AI_DOCUMENT_CONTENT_TYPE_OPTIONS,
} from "../../utils/aiDocumentVerification";
import {
  AiGlobalSettingsState,
  buildAiVerificationAdminSnapshot,
  buildApiToolRowSavePayload,
  buildApiToolsSavePayload,
  buildModelsSavePayload,
  buildPromptsSavePayload,
  buildSettingsSavePayload,
  isApiToolKeyRequired,
  mapPromptsFromApi,
  normalizeApiToolFromApi,
  normalizeGlobalSettingsFromApi,
  normalizeModelFromApi,
  getModelOutputTokenCap,
  getModelRecommendedMinTokens,
  getModelTokenHint,
} from "./aiVerificationAdminPayloads";
import "./document-verification.css";

interface AllProps {
  history: any;
}

type MainTab = "global" | "processes";

interface State {
  mainTab: MainTab;
  loading: boolean;
  saving: boolean;
  settings: any;
  prompts: AiProcessPromptConfig[];
  models: AiModelOption[];
  apiTools: AiApiToolOption[];
  apiToolKeyInputs: Record<string, string>;
  activeProcessTab: string;
  newProcessKey: string;
  newProcessLabel: string;
  /** JSON snapshot after last load/save — used for unsaved-change detection. */
  savedSnapshot: string;
  unsavedBarInset: { top: number; left: number; width: number; height: number } | null;
}

const AI_ADMIN_UNSAVED_MESSAGE =
  'You have unsaved changes to System Verification settings. Use "Save All Settings" before leaving, or your changes will be lost.';

function formatCloseRemarksForTextarea(options?: string[]): string {
  return (options && options.length ? options : []).join("\n");
}

function parseCloseRemarksFromTextarea(value: string): string[] {
  return value
    .split(/[\r\n,]+/)
    .map((s) => s.trim())
    .filter((s) => s !== "");
}

function newApiToolClientKey(): string {
  return `new_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function slugifyProcessKey(label: string): string {
  return label
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .substring(0, 32);
}

export default class AiVerificationManagement extends Component<AllProps, State> {
  private historyUnblock?: () => void;

  state: State = {
    mainTab: "global",
    loading: true,
    saving: false,
    settings: {},
    prompts: [],
    models: [],
    apiTools: [],
    apiToolKeyInputs: {},
    activeProcessTab: "process",
    newProcessKey: "",
    newProcessLabel: "",
    savedSnapshot: buildAiVerificationAdminSnapshot({
      settings: {},
      apiTools: [],
      models: [],
      prompts: [],
      apiToolKeyInputs: {},
    }),
    unsavedBarInset: null,
  };

  adminConfigSnapshot = (): string =>
    buildAiVerificationAdminSnapshot({
      settings: this.state.settings,
      apiTools: this.state.apiTools,
      models: this.state.models,
      prompts: this.state.prompts,
      apiToolKeyInputs: this.state.apiToolKeyInputs,
    });

  isAdminDirty = (): boolean => {
    if (this.state.loading) return false;
    return this.adminConfigSnapshot() !== this.state.savedSnapshot;
  };

  handleBeforeUnload = (e: BeforeUnloadEvent) => {
    if (!this.isAdminDirty()) return;
    e.preventDefault();
    e.returnValue = AI_ADMIN_UNSAVED_MESSAGE;
  };

  confirmLeaveAdmin = (): boolean => {
    if (!this.isAdminDirty()) return true;
    return window.confirm(`${AI_ADMIN_UNSAVED_MESSAGE}\n\nLeave without saving?`);
  };

  handleMainTabSelect = (nextTab: MainTab) => {
    if (nextTab === this.state.mainTab) return;
    if (!this.confirmLeaveAdmin()) return;
    this.setState({ mainTab: nextTab }, () => this.scheduleUnsavedBarLayout());
  };

  scheduleUnsavedBarLayout = () => {
    window.requestAnimationFrame(() => {
      this.updateUnsavedBarLayout();
      window.requestAnimationFrame(() => this.updateUnsavedBarLayout());
    });
  };

  updateUnsavedBarLayout = () => {
    if (!this.isAdminDirty()) {
      if (this.state.unsavedBarInset !== null) {
        this.setState({ unsavedBarInset: null });
      }
      return;
    }
    const header = document.querySelector(".pcoded-header") as HTMLElement | null;
    const main = document.querySelector(".pcoded-main-container") as HTMLElement | null;
    const top = header ? Math.ceil(header.getBoundingClientRect().bottom) : 85;
    const left = main ? Math.ceil(main.getBoundingClientRect().left) : 264;
    const width = main
      ? Math.ceil(main.getBoundingClientRect().width)
      : Math.max(0, window.innerWidth - left);
    const bar = document.querySelector(".ai-admin-unsaved-fixed") as HTMLElement | null;
    const height = bar ? Math.ceil(bar.getBoundingClientRect().height) : 52;
    const next = { top, left, width, height };
    const cur = this.state.unsavedBarInset;
    if (
      cur &&
      cur.top === next.top &&
      cur.left === next.left &&
      cur.width === next.width &&
      cur.height === next.height
    ) {
      return;
    }
    this.setState({ unsavedBarInset: next });
  };

  componentDidMount() {
    window.addEventListener("beforeunload", this.handleBeforeUnload);
    window.addEventListener("resize", this.scheduleUnsavedBarLayout);
    window.addEventListener("scroll", this.scheduleUnsavedBarLayout, true);
    if (this.props.history?.block) {
      this.historyUnblock = this.props.history.block(() => {
        if (!this.isAdminDirty()) return undefined;
        return AI_ADMIN_UNSAVED_MESSAGE;
      });
    }
    this.loadAll();
    this.scheduleUnsavedBarLayout();
  }

  componentDidUpdate(_prevProps: AllProps, prevState: State) {
    const dirtyNow = this.isAdminDirty();
    const dirtyWas =
      !prevState.loading &&
      buildAiVerificationAdminSnapshot({
        settings: prevState.settings,
        apiTools: prevState.apiTools,
        models: prevState.models,
        prompts: prevState.prompts,
        apiToolKeyInputs: prevState.apiToolKeyInputs,
      }) !== prevState.savedSnapshot;
    if (
      dirtyNow !== dirtyWas ||
      this.state.settings !== prevState.settings ||
      this.state.apiTools !== prevState.apiTools ||
      this.state.models !== prevState.models ||
      this.state.prompts !== prevState.prompts ||
      this.state.apiToolKeyInputs !== prevState.apiToolKeyInputs
    ) {
      this.scheduleUnsavedBarLayout();
    }
  }

  componentWillUnmount() {
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
    window.removeEventListener("resize", this.scheduleUnsavedBarLayout);
    window.removeEventListener("scroll", this.scheduleUnsavedBarLayout, true);
    if (this.historyUnblock) {
      this.historyUnblock();
      this.historyUnblock = undefined;
    }
  }

  loadAll = () => {
    this.setState({ loading: true });
    Promise.all([
      getAiVerificationSettings(),
      getAiVerificationPrompts(),
      getAiVerificationModels(),
      getAiVerificationApiTools(),
    ])
      .then(([settingsRes, promptsRes, modelsRes, apiToolsRes]) => {
        const settings = normalizeGlobalSettingsFromApi(
          settingsRes.success ? settingsRes.data : {},
        );
        const apiPrompts = promptsRes.success ? promptsRes.data : [];
        const models = modelsRes.success && Array.isArray(modelsRes.data)
          ? modelsRes.data.map((row: Record<string, unknown>) => normalizeModelFromApi(row))
          : [];
        const apiTools = apiToolsRes.success && Array.isArray(apiToolsRes.data)
          ? apiToolsRes.data.map((row: Record<string, unknown>) => normalizeApiToolFromApi(row))
          : [];
        const activeId =
          apiToolsRes.active_api_tool_id ||
          settings.active_api_tool_id ||
          0;
        const syncedTools = this.syncApiToolsEnabled(apiTools, Number(activeId) || 0);
        const prompts = mapPromptsFromApi(apiPrompts, settings);
        const nextState = {
          settings,
          prompts,
          models,
          apiTools: syncedTools,
          apiToolKeyInputs: {} as Record<string, string>,
          activeProcessTab: prompts[0]?.process_type || "process",
          loading: false,
        };
        this.setState({
          ...nextState,
          savedSnapshot: buildAiVerificationAdminSnapshot(nextState),
        });
      })
      .catch(() => {
        this.setState({ loading: false });
        ToastsStore.error("Failed to load system verification settings");
      });
  };

  getEnabledModels = (): AiModelOption[] => {
    return (this.state.models || []).filter((m) => Number(m.is_enabled) !== 0);
  };

  syncApiToolsEnabled = (tools: AiApiToolOption[], activeId: number): AiApiToolOption[] => {
    if (!tools.length) return tools;
    const hasEnabled = tools.some((t) => Number(t.is_enabled) === 1);
    if (activeId > 0) {
      return tools.map((t) => ({ ...t, is_enabled: t.id === activeId ? 1 : 0 }));
    }
    if (hasEnabled) {
      return tools;
    }
    return tools.map((t, i) => ({ ...t, is_enabled: i === 0 ? 1 : 0 }));
  };

  getEnabledApiToolIndex = (tools: AiApiToolOption[]): number => {
    const idx = tools.findIndex((t) => Number(t.is_enabled) === 1);
    return idx >= 0 ? idx : 0;
  };

  handleApiToolEnabledChange = (index: number, checked: boolean) => {
    const apiTools = this.state.apiTools.map((t, i) => ({
      ...t,
      is_enabled: checked && i === index ? 1 : 0,
    }));
    if (!checked && !apiTools.some((t) => Number(t.is_enabled) === 1) && apiTools.length > 0) {
      apiTools[0] = { ...apiTools[0], is_enabled: 1 };
    }
    this.setState({ apiTools });
  };

  handleSettingChange = (field: string, value: any) => {
    this.setState((prev) => {
      const settings = { ...prev.settings, [field]: value };
      if (field === "openai_model" && typeof value === "string") {
        settings.max_tokens = getModelRecommendedMinTokens(value);
      }
      return { settings };
    });
  };

  handlePromptChange = (index: number, field: string, value: any) => {
    const prompts = [...this.state.prompts];
    const updated = { ...prompts[index], [field]: value };
    if (field === "openai_model" && typeof value === "string") {
      updated.max_tokens = getModelRecommendedMinTokens(value);
    }
    prompts[index] = updated;
    this.setState({ prompts });
  };

  handleModelRowChange = (index: number, field: string, value: any) => {
    const models = [...this.state.models];
    models[index] = { ...models[index], [field]: value };
    this.setState({ models });
  };

  getApiToolRowKey = (tool: AiApiToolOption, index: number): string => {
    return tool._client_key || (tool.id ? `id_${tool.id}` : `idx_${index}`);
  };

  handleApiToolRowChange = (index: number, field: string, value: any) => {
    const apiTools = [...this.state.apiTools];
    apiTools[index] = { ...apiTools[index], [field]: value };
    this.setState({ apiTools });
  };

  setApiToolKeyInput = (rowKey: string, value: string) => {
    this.setState((prev) => ({
      apiToolKeyInputs: { ...prev.apiToolKeyInputs, [rowKey]: value },
    }));
  };

  addApiToolRow = () => {
    const clientKey = newApiToolClientKey();
    this.setState({
      apiTools: [
        ...this.state.apiTools,
        {
          _client_key: clientKey,
          tool_label: "",
          api_endpoint_url: "",
          is_enabled: this.state.apiTools.length === 0 ? 1 : 0,
        },
      ],
    });
  };

  removeApiToolRow = (index: number) => {
    const tool = this.state.apiTools[index];
    if (!tool) return;
    if (this.state.apiTools.length <= 1) {
      ToastsStore.error("At least one system tool connection must remain");
      return;
    }
    const label = tool.tool_label || "this connection";
    if (!window.confirm(`Remove "${label}" from the system tool master? Click Save All Settings to apply.`)) {
      return;
    }
    let apiTools = this.state.apiTools.filter((_, i) => i !== index);
    if (!apiTools.some((t) => Number(t.is_enabled) === 1) && apiTools.length > 0) {
      apiTools = apiTools.map((t, i) => ({ ...t, is_enabled: i === 0 ? 1 : 0 }));
    }
    this.setState({ apiTools });
    ToastsStore.success("System tool removed. Click Save All Settings to apply.");
  };

  addModelRow = () => {
    this.setState({
      models: [
        ...this.state.models,
        {
          model_id: "",
          model_label: "",
          is_enabled: 1,
        },
      ],
    });
  };

  removeModelRow = (index: number) => {
    const model = this.state.models[index];
    if (!model) return;
    if (this.state.models.length <= 1) {
      ToastsStore.error("At least one model must remain in the list");
      return;
    }
    const label = model.model_label || model.model_id || "this model";
    if (!window.confirm(`Remove "${label}" from the model list? Click Save All Settings to apply.`)) {
      return;
    }
    const models = this.state.models.filter((_, i) => i !== index);
    this.setState({ models });
    ToastsStore.success("Model removed. Click Save All Settings to apply.");
  };

  addProcessType = () => {
    const { newProcessLabel, newProcessKey, settings, prompts } = this.state;
    const key = (newProcessKey || slugifyProcessKey(newProcessLabel)).trim();
    const label = newProcessLabel.trim();

    if (!key || !label) {
      ToastsStore.error("Process key and label are required");
      return;
    }
    if (!/^[a-z][a-z0-9_]{0,31}$/.test(key)) {
      ToastsStore.error("Process key must be lowercase letters, numbers, underscore (start with letter)");
      return;
    }
    if (prompts.some((p) => p.process_type === key)) {
      ToastsStore.error("Process key already exists");
      return;
    }

    const newPrompt: AiProcessPromptConfig = {
      process_type: key,
      process_label: label,
      is_enabled: 1,
      is_system: 0,
      _isNew: true,
      prompt_title: label,
      openai_model: settings.openai_model || "gpt-4o-mini",
      max_tokens: settings.max_tokens || 4096,
      temperature: settings.temperature || 0.2,
      max_retries: settings.max_retries ?? 3,
      retry_delay_ms: settings.retry_delay_ms ?? 2000,
      system_prompt:
        "You are a legal document verifier. Respond ONLY with valid JSON: {\"errors\":[...]}",
      user_prompt:
        "Review the document and list all errors with file_name, page_number, section_field_name, error_description, suggested_correction, severity.\n\nCheck for:\n- Name mismatch\n- Missing fields\n- Date validation",
      document_content_type: "word",
      send_ec_document: 0,
      close_remark_options: [...DEFAULT_CLOSE_REMARK_OPTIONS],
    };

    this.setState({
      prompts: [...prompts, newPrompt],
      activeProcessTab: key,
      newProcessKey: "",
      newProcessLabel: "",
      mainTab: "processes",
    });
    ToastsStore.success("Process type added. Configure prompt and model, then Save All.");
  };

  canDeleteProcess = (processType: string): boolean => {
    const prompt = this.state.prompts.find((p) => p.process_type === processType);
    if (!prompt) return false;
    return Number(prompt.is_system) !== 1;
  };

  removeProcessFromState = (processType: string) => {
    const prompts = this.state.prompts.filter((p) => p.process_type !== processType);
    const nextTab =
      this.state.activeProcessTab === processType
        ? prompts[0]?.process_type || "process"
        : this.state.activeProcessTab;
    this.setState({ prompts, activeProcessTab: nextTab });
  };

  deleteProcessType = (processType: string, options?: { skipConfirm?: boolean }) => {
    if (!this.canDeleteProcess(processType)) {
      ToastsStore.error("This process type is marked as system and cannot be deleted");
      return;
    }

    const prompt = this.state.prompts.find((p) => p.process_type === processType);
    if (!prompt) return;

    const label = prompt.process_label || processType;
    if (
      !options?.skipConfirm &&
      !window.confirm(`Remove process type "${label}"? This cannot be undone for saved processes.`)
    ) {
      return;
    }

    if (prompt._isNew) {
      this.removeProcessFromState(processType);
      ToastsStore.success("Process type removed");
      return;
    }

    const user: any = localStorage.getItem("user");
    const userId = user ? JSON.parse(user)["userId"] : "";

    deleteAiVerificationProcess(processType, userId)
      .then((res) => {
        if (res.success) {
          ToastsStore.success("Process type deleted");
          this.loadAll();
        } else if ((res.message || "").toLowerCase().includes("not found")) {
          this.removeProcessFromState(processType);
          ToastsStore.success("Process type removed");
        } else {
          ToastsStore.error(res.message || "Delete failed");
        }
      })
      .catch(() => ToastsStore.error("Delete failed"));
  };

  saveAll = () => {
    const user: any = localStorage.getItem("user");
    const userId = user ? JSON.parse(user)["userId"] : "";
    const {
      settings,
      prompts,
      models,
      apiTools,
      apiToolKeyInputs,
    } = this.state;

    const labeledTools = apiTools.filter((t) => (t.tool_label || "").trim() !== "");
    const toolsPayload = labeledTools.map((t, i) => {
      const rowKey = t._client_key || (t.id ? `id_${t.id}` : `idx_${i}`);
      const keyInput = apiToolKeyInputs[rowKey] || "";
      return buildApiToolRowSavePayload(t, i, keyInput);
    });

    if (!toolsPayload.length) {
      ToastsStore.error("Add at least one system tool with a tool name");
      return;
    }

    const enabledIndex = this.getEnabledApiToolIndex(labeledTools);
    const enabledTool = labeledTools[enabledIndex] || labeledTools[0];
    const enabledPayload = toolsPayload[enabledIndex] || toolsPayload[0];
    if (!enabledPayload || Number(enabledPayload.is_enabled) !== 1) {
      ToastsStore.error("Enable one system tool for verification");
      return;
    }

    for (let i = 0; i < toolsPayload.length; i++) {
      const t = toolsPayload[i];
      const source = labeledTools[i];
      const rowKey = source?._client_key || (source?.id ? `id_${source.id}` : `idx_${i}`);
      const keyInput = (apiToolKeyInputs[rowKey] || "").trim();
      if (!/^https?:\/\//i.test(String(t.api_endpoint_url || ""))) {
        ToastsStore.error(`"${t.tool_label}": enter a valid endpoint URL (http:// or https://)`);
        return;
      }
      if (source && isApiToolKeyRequired(source, keyInput)) {
        ToastsStore.error(`"${t.tool_label}": API key is required`);
        return;
      }
    }

    this.setState({ saving: true });
    saveAiVerificationApiTools(
      buildApiToolsSavePayload(
        toolsPayload,
        enabledTool?.id || undefined,
        !enabledTool?.id && enabledTool?._client_key ? enabledTool._client_key : undefined,
        userId,
      ),
    )
      .then((res) => {
        if (!res.success) throw new Error(res.message);
        const activeId = res.active_api_tool_id || enabledTool?.id || 0;
        return saveAiVerificationSettings(
          buildSettingsSavePayload(settings as AiGlobalSettingsState, activeId, userId),
        );
      })
      .then((res) => {
        if (!res.success) throw new Error(res.message);
        return saveAiVerificationModels(buildModelsSavePayload(models, userId));
      })
      .then((res) => {
        if (!res.success) throw new Error(res.message);
        return saveAiVerificationPrompts(buildPromptsSavePayload(prompts, userId));
      })
      .then((res) => {
        this.setState({ saving: false });
        if (res.success) {
          const savedCount = Number(res.saved ?? 0);
          if (savedCount === 0 && prompts.length > 0) {
            ToastsStore.error(
              res.message || "Process prompts were not saved. Run DB migrations on the server.",
            );
            return;
          }
          ToastsStore.success("All settings saved successfully");
          this.loadAll();
        } else {
          ToastsStore.error(res.message || "Failed to save prompts");
        }
      })
      .catch((err) => {
        this.setState({ saving: false });
        ToastsStore.error(err.message || "Save failed");
      });
  };

  renderUnsavedFixedBar = () => {
    if (typeof document === "undefined" || !this.isAdminDirty()) return null;
    const { saving, loading, unsavedBarInset } = this.state;
    const inset = unsavedBarInset;
    const style: CSSProperties = {
      top: inset?.top ?? 85,
      left: inset?.left ?? 264,
      width: inset?.width ?? undefined,
      right: inset ? undefined : 0,
    };
    return ReactDOM.createPortal(
      <div className="ai-admin-unsaved-fixed" style={style} role="status" aria-live="polite">
        <div className="ai-admin-unsaved-fixed-inner">
          <span className="ai-admin-unsaved-fixed-text">
            <strong>Unsaved changes.</strong> You edited system tools, models, or process prompts. Save
            before reloading, switching tabs, or leaving this page.
          </span>
          <button
            type="button"
            className="btn btn-warning btn-sm font-weight-bold px-4"
            disabled={saving || loading}
            onClick={this.saveAll}
          >
            {saving ? "Saving…" : "Save All Settings"}
          </button>
        </div>
      </div>,
      document.body,
    );
  };

  renderProcessEditor() {
    const { prompts, activeProcessTab } = this.state;
    const index = prompts.findIndex((p) => p.process_type === activeProcessTab);
    if (index < 0) return null;
    const prompt = prompts[index];
    const modelOptions = this.getEnabledModels();
    const isSystem = Number(prompt.is_system) === 1;
    const canDelete = this.canDeleteProcess(prompt.process_type);

    return (
      <div className="ai-settings-section ai-process-prompt-section">
        <div className="ai-settings-section-head">
          <div>
            <h5 className="mb-1">{prompt.process_label || prompt.process_type}</h5>
            <small className="text-muted">
              Key: <code>{prompt.process_type}</code>
              {isSystem
                ? " · System process (cannot delete)"
                : " · Custom process (use Delete to remove)"}
            </small>
          </div>
          <div className="d-flex align-items-center" style={{ gap: 8 }}>
            <label className="ai-enable-toggle mb-0">
              <input
                type="checkbox"
                checked={!!Number(prompt.is_enabled)}
                onChange={(e) => this.handlePromptChange(index, "is_enabled", e.target.checked ? 1 : 0)}
              />
              <span>Enabled</span>
            </label>
            {canDelete ? (
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                title="Delete custom process type"
                onClick={() => this.deleteProcessType(prompt.process_type)}
              >
                <i className="fa fa-trash" /> Delete
              </button>
            ) : null}
          </div>
        </div>

        <div className="ai-settings-grid">
          <div className="form-group">
            <label>Display Label</label>
            <input
              className="form-control"
              value={prompt.process_label || ""}
              onChange={(e) => this.handlePromptChange(index, "process_label", e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Document Content-Type</label>
            <select
              className="form-control"
              value={prompt.document_content_type === "pdf" ? "pdf" : "word"}
              onChange={(e) =>
                this.handlePromptChange(
                  index,
                  "document_content_type",
                  e.target.value === "pdf" ? "pdf" : "word",
                )
              }
            >
              {AI_DOCUMENT_CONTENT_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <small className="text-muted d-block mt-1">
              PDF mode converts Word to PDF via the conversion service before AI verification.
            </small>
          </div>
          <div className="form-group">
            <label className="d-flex align-items-center mb-0">
              <input
                type="checkbox"
                className="mr-2"
                checked={!!prompt.send_ec_document}
                onChange={(e) =>
                  this.handlePromptChange(index, "send_ec_document", e.target.checked ? 1 : 0)
                }
              />
              Send EC Document to OpenAI for AI Verification
            </label>
            <small className="text-muted d-block mt-1">
              When enabled, all active EC documents for the file are sent with the legal document.
            </small>
          </div>
          <div className="form-group">
            <label>OpenAI Model (process-wise)</label>
            <select
              className="form-control"
              value={prompt.openai_model || this.state.settings.openai_model || ""}
              onChange={(e) => this.handlePromptChange(index, "openai_model", e.target.value)}
            >
              {prompt.openai_model &&
              !modelOptions.some((m) => m.model_id === prompt.openai_model) ? (
                <option value={prompt.openai_model}>{prompt.openai_model}</option>
              ) : null}
              {modelOptions.map((m) => (
                <option key={m.model_id} value={m.model_id}>
                  {m.model_label || m.model_id}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Max Tokens</label>
            <input
              type="number"
              className="form-control"
              min={getModelRecommendedMinTokens(prompt.openai_model || this.state.settings.openai_model || "")}
              max={getModelOutputTokenCap(prompt.openai_model || this.state.settings.openai_model || "")}
              value={prompt.max_tokens || this.state.settings.max_tokens || 4096}
              onChange={(e) => this.handlePromptChange(index, "max_tokens", Number(e.target.value))}
            />
            <small className="text-muted d-block mt-1">
              Model limit: {getModelOutputTokenCap(prompt.openai_model || this.state.settings.openai_model || "").toLocaleString()} completion tokens.
              Recommended minimum for this model: {getModelRecommendedMinTokens(prompt.openai_model || this.state.settings.openai_model || "").toLocaleString()}
              {" "}
              ({getModelTokenHint(prompt.openai_model || this.state.settings.openai_model || "")})
            </small>
          </div>
          <div className="form-group">
            <label>Temperature</label>
            <input
              type="number"
              step="0.1"
              min={0}
              max={2}
              className="form-control"
              value={prompt.temperature ?? this.state.settings.temperature ?? 0.2}
              onChange={(e) => this.handlePromptChange(index, "temperature", Number(e.target.value))}
            />
          </div>
          <div className="form-group">
            <label>Max Retries</label>
            <input
              type="number"
              className="form-control"
              min={0}
              max={10}
              value={prompt.max_retries ?? this.state.settings.max_retries ?? 3}
              onChange={(e) => this.handlePromptChange(index, "max_retries", Number(e.target.value))}
            />
          </div>
          <div className="form-group">
            <label>Retry Delay (ms)</label>
            <input
              type="number"
              className="form-control"
              min={0}
              step={100}
              value={prompt.retry_delay_ms ?? this.state.settings.retry_delay_ms ?? 2000}
              onChange={(e) => this.handlePromptChange(index, "retry_delay_ms", Number(e.target.value))}
            />
          </div>
        </div>

        <div className="form-group">
          <label>Prompt Title</label>
          <input
            className="form-control"
            value={prompt.prompt_title || ""}
            onChange={(e) => this.handlePromptChange(index, "prompt_title", e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>System Prompt</label>
          <textarea
            className="form-control"
            rows={3}
            value={prompt.system_prompt || ""}
            onChange={(e) => this.handlePromptChange(index, "system_prompt", e.target.value)}
          />
          <small className="text-muted">System role and JSON output format only.</small>
        </div>
        <div className="form-group mb-0">
          <label>Verification Prompt</label>
          <textarea
            className="form-control"
            rows={10}
            placeholder="Enter full verification instructions, checks, and output format in one place..."
            value={prompt.user_prompt || ""}
            onChange={(e) => this.handlePromptChange(index, "user_prompt", e.target.value)}
          />
          <small className="text-muted">
            Single prompt for this process — include what to check, rules, and required JSON response. Document text is appended automatically when verification runs.
          </small>
        </div>
        <div className="form-group mb-0">
          <label>Close error remark options</label>
          <textarea
            className="form-control"
            rows={3}
            placeholder={"Corrected\nNo Need"}
            value={formatCloseRemarksForTextarea(prompt.close_remark_options)}
            onChange={(e) =>
              this.handlePromptChange(
                index,
                "close_remark_options",
                parseCloseRemarksFromTextarea(e.target.value)
              )
            }
          />
          <small className="text-muted">
            One option per line. These appear in the Close Error dropdown for this process on work files.
          </small>
        </div>
      </div>
    );
  }

  render() {
    const {
      loading,
      saving,
      settings,
      activeProcessTab,
      prompts,
      mainTab,
      models,
      apiTools,
      apiToolKeyInputs,
      newProcessKey,
      newProcessLabel,
    } = this.state;

    const modelOptions = this.getEnabledModels();
    const adminDirty = this.isAdminDirty();

    return (
      <section className="pcoded-main-container ai-verification-page-shell">
        <ToastsContainer store={ToastsStore} position={ToastsContainerPosition.TOP_RIGHT} />
        {adminDirty && this.renderUnsavedFixedBar()}
        <div className="pcoded-wrapper">
          <div className="pcoded-content">
            <div className="pcoded-inner-content">
              <div className="main-body">
                <div className="page-wrapper">
                  <div className="row mt-3">
                    <div className="col-sm-12">
                      <div
                        className={`card ai-admin-card${
                          adminDirty ? " ai-admin-card-header-dirty" : ""
                        }`}
                      >
                        <div className="card-header d-flex justify-content-between align-items-start flex-wrap ai-admin-page-header">
                          <div className="ai-admin-page-title">
                            <h5>System Verification — Dynamic Management</h5>
                            <p className="text-muted mb-0 small">
                              Global API &amp; model settings, and process-wise verification prompts.
                            </p>
                          </div>
                          <div className="d-flex align-items-center mt-1">
                            {adminDirty && !loading ? (
                              <span className="small text-warning font-weight-bold mr-2">Unsaved</span>
                            ) : null}
                            <button
                              className={`btn btn-sm ${adminDirty ? "btn-warning" : "btn-primary"}`}
                              disabled={saving || loading}
                              onClick={this.saveAll}
                            >
                              {saving ? (
                                <i className="fa fa-spinner fa-spin" />
                              ) : (
                                "Save All Settings"
                              )}
                            </button>
                          </div>
                        </div>
                        <div className="card-body">
                          {adminDirty ? (
                            <div
                              className="ai-admin-unsaved-fixed-spacer"
                              aria-hidden="true"
                              style={{ height: this.state.unsavedBarInset?.height ?? 52 }}
                            />
                          ) : null}
                          {loading ? (
                            <div className="ai-admin-loading">
                              <i className="fa fa-spinner fa-spin fa-2x" />
                            </div>
                          ) : (
                            <>
                              <ul className="nav nav-tabs ai-admin-tabs">
                                <li className="nav-item">
                                  <button
                                    type="button"
                                    className={`nav-link ${mainTab === "global" ? "active" : ""}`}
                                    onClick={() => this.handleMainTabSelect("global")}
                                  >
                                    Global API & Models
                                  </button>
                                </li>
                                <li className="nav-item">
                                  <button
                                    type="button"
                                    className={`nav-link ${mainTab === "processes" ? "active" : ""}`}
                                    onClick={() => this.handleMainTabSelect("processes")}
                                  >
                                    Process Management
                                  </button>
                                </li>
                              </ul>

                              {mainTab === "global" && (
                                <div className="ai-settings-section">
                                  <div className="ai-enable-banner">
                                    <label className="ai-enable-toggle">
                                      <input
                                        type="checkbox"
                                        checked={!!Number(settings.is_enabled)}
                                        onChange={(e) =>
                                          this.handleSettingChange("is_enabled", e.target.checked ? 1 : 0)
                                        }
                                      />
                                      <span>Enable System Document Verification</span>
                                    </label>
                                  </div>

                                  <div className="ai-settings-section ai-global-api-tools-section">
                                    <div className="ai-settings-section-head">
                                      <h5>1. System Tools</h5>
                                      <button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm"
                                        onClick={this.addApiToolRow}
                                      >
                                        + Add System Tool
                                      </button>
                                    </div>
                                    <p className="text-muted small mb-2">
                                      Add each system connection: <strong>tool name</strong>, <strong>API key</strong>, and{" "}
                                      <strong>endpoint URL</strong>. Enable the row used for verification (one at a time).
                                    </p>
                                    <div className="table-responsive">
                                      <table className="table table-bordered table-sm ai-api-tools-master-table">
                                        <thead className="thead-light">
                                          <tr>
                                            <th>Tool Name</th>
                                            <th>API Key</th>
                                            <th>Endpoint URL</th>
                                            <th style={{ width: 88 }}>Enabled</th>
                                            <th style={{ width: 80 }}>Action</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {apiTools.map((tool, index) => {
                                            const rowKey = this.getApiToolRowKey(tool, index);
                                            const isEnabledRow = Number(tool.is_enabled) === 1;
                                            return (
                                              <tr
                                                key={rowKey}
                                                className={isEnabledRow ? "ai-api-tool-row-active" : ""}
                                              >
                                                <td>
                                                  <input
                                                    className="form-control form-control-sm"
                                                    placeholder="e.g. OpenAI Production"
                                                    value={tool.tool_label || ""}
                                                    onChange={(e) =>
                                                      this.handleApiToolRowChange(
                                                        index,
                                                        "tool_label",
                                                        e.target.value,
                                                      )
                                                    }
                                                  />
                                                </td>
                                                <td>
                                                  <input
                                                    type="password"
                                                    className="form-control form-control-sm"
                                                    placeholder={
                                                      tool.openai_api_key_masked || "Enter API key"
                                                    }
                                                    value={apiToolKeyInputs[rowKey] || ""}
                                                    onChange={(e) =>
                                                      this.setApiToolKeyInput(rowKey, e.target.value)
                                                    }
                                                  />
                                                </td>
                                                <td>
                                                  <input
                                                    type="url"
                                                    className="form-control form-control-sm"
                                                    placeholder="https://api.openai.com/v1/chat/completions"
                                                    value={tool.api_endpoint_url || ""}
                                                    onChange={(e) =>
                                                      this.handleApiToolRowChange(
                                                        index,
                                                        "api_endpoint_url",
                                                        e.target.value,
                                                      )
                                                    }
                                                  />
                                                </td>
                                                <td className="text-center align-middle">
                                                  <input
                                                    type="checkbox"
                                                    checked={isEnabledRow}
                                                    title="Use this connection for verification"
                                                    onChange={(e) =>
                                                      this.handleApiToolEnabledChange(index, e.target.checked)
                                                    }
                                                  />
                                                </td>
                                                <td className="text-center align-middle">
                                                  <button
                                                    type="button"
                                                    className="btn btn-outline-danger btn-sm"
                                                    title="Remove system tool"
                                                    disabled={apiTools.length <= 1}
                                                    onClick={() => this.removeApiToolRow(index)}
                                                  >
                                                    <i className="fa fa-trash" />
                                                  </button>
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>

                                  <div className="ai-settings-section ai-global-models-section">
                                    <div className="ai-settings-section-head">
                                      <h5>2. Model Master</h5>
                                      <button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm"
                                        onClick={this.addModelRow}
                                      >
                                        + Add Model
                                      </button>
                                    </div>
                                    <p className="text-muted small mb-2">
                                      Models available for process verification. Add model IDs from your active system tool provider.
                                    </p>
                                    <div className="table-responsive">
                                      <table className="table table-bordered table-sm">
                                        <thead className="thead-light">
                                          <tr>
                                            <th>Model ID</th>
                                            <th>Label</th>
                                            <th>Enabled</th>
                                            <th style={{ width: 80 }}>Action</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {models.map((m, index) => (
                                            <tr key={index}>
                                              <td>
                                                <input
                                                  className="form-control form-control-sm"
                                                  value={m.model_id}
                                                  onChange={(e) =>
                                                    this.handleModelRowChange(index, "model_id", e.target.value)
                                                  }
                                                />
                                              </td>
                                              <td>
                                                <input
                                                  className="form-control form-control-sm"
                                                  value={m.model_label}
                                                  onChange={(e) =>
                                                    this.handleModelRowChange(index, "model_label", e.target.value)
                                                  }
                                                />
                                              </td>
                                              <td className="text-center">
                                                <input
                                                  type="checkbox"
                                                  checked={!!Number(m.is_enabled)}
                                                  onChange={(e) =>
                                                    this.handleModelRowChange(
                                                      index,
                                                      "is_enabled",
                                                      e.target.checked ? 1 : 0,
                                                    )
                                                  }
                                                />
                                              </td>
                                              <td className="text-center align-middle">
                                                <button
                                                  type="button"
                                                  className="btn btn-outline-danger btn-sm"
                                                  title="Remove model"
                                                  disabled={models.length <= 1}
                                                  onClick={() => this.removeModelRow(index)}
                                                >
                                                  <i className="fa fa-trash" />
                                                </button>
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>

                                  <div className="ai-settings-section ai-global-defaults-section">
                                    <h5 className="mb-3">3. Default Settings</h5>
                                    <div className="ai-settings-grid">
                                      <div className="form-group">
                                        <label>Default Model (fallback)</label>
                                        <select
                                          className="form-control"
                                          value={settings.openai_model || "gpt-4o-mini"}
                                          onChange={(e) => this.handleSettingChange("openai_model", e.target.value)}
                                        >
                                          {modelOptions.map((m) => (
                                            <option key={m.model_id} value={m.model_id}>
                                              {m.model_label || m.model_id}
                                            </option>
                                          ))}
                                        </select>
                                        <small className="text-muted">Used when a process has no model set.</small>
                                      </div>
                                      <div className="form-group">
                                        <label>Max Retries</label>
                                        <input
                                          type="number"
                                          className="form-control"
                                          value={settings.max_retries ?? 3}
                                          onChange={(e) =>
                                            this.handleSettingChange("max_retries", Number(e.target.value))
                                          }
                                        />
                                      </div>
                                      <div className="form-group">
                                        <label>Retry Delay (ms)</label>
                                        <input
                                          type="number"
                                          className="form-control"
                                          value={settings.retry_delay_ms ?? 2000}
                                          onChange={(e) =>
                                            this.handleSettingChange("retry_delay_ms", Number(e.target.value))
                                          }
                                        />
                                      </div>
                                      <div className="form-group">
                                        <label>Default Max Tokens</label>
                                        <input
                                          type="number"
                                          className="form-control"
                                          min={getModelRecommendedMinTokens(settings.openai_model || "")}
                                          max={getModelOutputTokenCap(settings.openai_model || "")}
                                          value={settings.max_tokens ?? 4096}
                                          onChange={(e) =>
                                            this.handleSettingChange("max_tokens", Number(e.target.value))
                                          }
                                        />
                                        <small className="text-muted d-block mt-1">
                                          Model limit: {getModelOutputTokenCap(settings.openai_model || "").toLocaleString()} tokens.
                                          Recommended minimum: {getModelRecommendedMinTokens(settings.openai_model || "").toLocaleString()}.
                                          {" "}
                                          ({getModelTokenHint(settings.openai_model || "")})
                                        </small>
                                      </div>
                                      <div className="form-group">
                                        <label>Default Temperature</label>
                                        <input
                                          type="number"
                                          step="0.1"
                                          className="form-control"
                                          value={settings.temperature ?? 0.2}
                                          onChange={(e) =>
                                            this.handleSettingChange("temperature", Number(e.target.value))
                                          }
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {mainTab === "processes" && (
                                <>
                                  <div className="ai-settings-section mb-3">
                                    <h6 className="mb-2">Add Custom Process Type</h6>
                                    <div className="ai-settings-grid ai-settings-grid-2">
                                      <div className="form-group">
                                        <label>Process Key (slug)</label>
                                        <input
                                          className="form-control"
                                          placeholder="e.g. final_review"
                                          value={newProcessKey}
                                          onChange={(e) => this.setState({ newProcessKey: e.target.value })}
                                        />
                                      </div>
                                      <div className="form-group">
                                        <label>Display Label</label>
                                        <input
                                          className="form-control"
                                          placeholder="e.g. Final Review"
                                          value={newProcessLabel}
                                          onChange={(e) => this.setState({ newProcessLabel: e.target.value })}
                                        />
                                      </div>
                                    </div>
                                    <button type="button" className="btn btn-outline-primary btn-sm" onClick={this.addProcessType}>
                                      + Add Process Type
                                    </button>
                                  </div>

                                  <ul className="ai-process-tabs mb-3">
                                    {prompts.map((pt) => {
                                      const canDelete = this.canDeleteProcess(pt.process_type);
                                      const isActive = activeProcessTab === pt.process_type;
                                      return (
                                        <li className="ai-process-tab-item" key={pt.process_type}>
                                          <div
                                            className={`ai-process-tab-pill${isActive ? " active" : ""}${
                                              canDelete ? " has-remove" : ""
                                            }`}
                                          >
                                            <button
                                              type="button"
                                              className="ai-process-tab-label"
                                              onClick={() => this.setState({ activeProcessTab: pt.process_type })}
                                            >
                                              {pt.process_label || pt.process_type}
                                            </button>
                                            {canDelete && (
                                              <button
                                                type="button"
                                                className="ai-process-tab-remove"
                                                title={`Delete ${pt.process_label || pt.process_type}`}
                                                aria-label={`Delete ${pt.process_label || pt.process_type}`}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  this.deleteProcessType(pt.process_type);
                                                }}
                                              >
                                                <i className="fa fa-times" aria-hidden="true" />
                                              </button>
                                            )}
                                          </div>
                                        </li>
                                      );
                                    })}
                                  </ul>

                                  {!prompts.length ? (
                                    <p className="text-muted small mb-3">
                                      No process prompts loaded from the server. Check the database and refresh, or add a
                                      custom process type.
                                    </p>
                                  ) : null}

                                  {this.renderProcessEditor()}
                                </>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }
}
